/**
 * @deprecated use the functions from the category modules instead
 */



/**
 * **import { trimText } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Returns a trimmed text which does not cut words.
 * Adds " ..." to the end if trimmed.
 *
 * @param text  the text to be trimmed
 * @param max   the maximum amount of characters in the trimmed text (including " ...")
 * @param min   the minimum of words which should be returned (optional)
 */
export function trimText(text: string, max: number, min: number = 0): string {

  let trimmed = text.substring(0, max);
  trimmed = trimmed.substring(0, Math.min(trimmed.length, trimmed.lastIndexOf(" ")));

  if (trimmed.length != text.length) {
    max -= 4;
    trimmed = text.substring(0, max);
    trimmed = trimmed.substring(0, Math.min(trimmed.length, trimmed.lastIndexOf(" ")));

    const slices = text.split(' ');
    const slices_text = [];
    for (let i=0, l=slices.length; i<l; i++) {
      if (min <= 0) break;
      if (slices[i].trim()) min--;
      slices_text.push(slices[i]);
    }
    if (slices.length == slices_text.length) return text;
    if (trimmed.length < slices_text.join(' ').length) trimmed = slices_text.join(' ');

    if (!trimmed.length) trimmed = '...';
    else trimmed += ' ...';
  }

  return trimmed;
}

/**
 * **import { capitalizeFirstLetter } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Capitalizes the first letter of a string.
 *
 * @param str  the string to be capitalized
 */
export function capitalizeFirstLetter(str: string) {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * **import { camelize } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Converts a text with hyphen separate words into a camelized version.
 *
 * @param text    the text to be camelized
 * @param lower   lowers the case of the remaining characters
 */
export function camelise(text: string, lower: boolean = false): string {

  const split = text.split('-');
  text = split[0];
  for (let i=1, l=split.length; i<l; i++) {
    text += split[i].substring(0, 1).toUpperCase();
    if (lower) text += split[i].substring(1).toLowerCase();
    else text += split[i].substring(1);
  }

  return text;
}

/**
 * **import { cutText } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Cuts a text if it exceeds a maximum amount of characters.
 * Adds "..." if text got cut.
 *
 * @param text   the text to be cut
 * @param max    the maximum amount of characters in the cut text (including "...")
 */
export function cutText(text: string, max: number): string {

  if (max < 3) max = 3;

  if (text.length > max) {
    if (text.length < 3) text = '...';
    else text = text.substr(0, max-3) + '...';
  }

  return text;
}


/**
 * **import { cutLink } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Cuts a link if it exceeds a maximum amount of characters.
 * Adds "..." if text got cut.
 * Removes the protocol and "www".
 *
 * @param text   the text to be cut
 * @param max    the maximum amount of characters in the cut text (including "...")
 */
export function cutLink(link: string, max: number): string {

  link = link.replace(/(^\w+:|^)\/\//, "").replace("www.", "");
  link = link.endsWith("/") ? link.slice(0, -1) : link;

  return cutText(link, max);
}

/**
 * **import { removeProtocol } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Removes the protocol from a link.
 *
 * @param link  the link from which the protocol should be removed
 */
export function removeProtocol(link: string): string {

  link = link.replace(/(^\w+:|^)\/\//, "");
  link = link.endsWith("/") ? link.slice(0, -1) : link;

  return link;
}

/**
 * **import { stripHtml } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Removes html tags.
 * Uses regular expansions to extract the tags.
 *
 * @param text  the text from which the html tags get striped
 * @param tag   strips only the specified tag (optional)
 */
export function stripHtml(text: string, tag?: string): string {

  if (!text) return text;

  let reg_exp = new RegExp('<[^>]*>', 'g');
  if (tag) reg_exp = new RegExp(`</?${tag}((>)|( [^>]*>)){1}`, 'g');
  text = text.replace(reg_exp, '');

  return text;
}

/**
 * **import { extractPlainText } from '@src/hylo-components/utils/usefull/usefull';**
 * 
 * Get plain text from html string
 * 
 * Similar to the stripHtml function but also removes new lines and tabs.
 * Also converts html entities like `&amp;` to their corresponding characters.
 * Uses "textContent" to remove the html tags.
 * 
 * @param htmlString The html string
 * @see stripHtml
 */
export function extractPlainText(htmlString: string) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');
  return doc.body.textContent || "";
}

/**
 * **import { getTag } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Returns the "index" appearance of the "tag" in the "text".
 *
 * @param text  the text to search for the "tag"
 * @param tag   the tag to search for
 * @param index the index of the "tag"s appearance (optional, 1 -> first (default), 2 -> second etc.)
 */
export function getTag(text: string, tag: string, index: number = 1): string {

  const reg_exp = new RegExp(`<${tag}((>)|( [^>]*>)){1}([\\s\\S]*?)</${tag}((>)|( [^>]*>)){1}`, 'g');

  let counter = 0;
  let result = null;
  while (result = reg_exp.exec(text)) {
    counter++;
    if (counter == index) return result[0];
  }

  return null;
}

/**
 * **import { stripMarkdown } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Removes markdown syntax.
 *
 * @param text  the text from which the markdown get striped
 */
export function stripMarkdown(text: string): string {

  // Remove horizontal rules (stripListHeaders conflict with this rule, which is why it has been moved to the top)
  text = text.replace(/^(-\s*?|\*\s*?|_\s*?){3,}\s*$/gm, '');

//  text = text.replace(/^([\s\t]*)([\*\-\+]|\d+\.)\s+/gm, '$1');

  text = text
  // Header
  .replace(/\n={2,}/g, '\n')
  // Fenced codeblocks
  .replace(/~{3}.*\n/g, '')
  // Strikethrough
  .replace(/~~/g, '')
  // Fenced codeblocks
  .replace(/`{3}.*\n/g, '');

  text = text
  // Remove HTML tags
  .replace(/<[^>]*>/g, '')
  // Remove setext-style headers
  .replace(/^[=\-]{2,}\s*$/g, '')
  // Remove footnotes?
  .replace(/\[\^.+?\](\: .*?$)?/g, '')
  .replace(/\s{0,2}\[.*?\]: .*?$/g, '')
  // Remove images
  .replace(/\!\[(.*?)\][\[\(].*?[\]\)]/g, '')
  // Remove inline links
  .replace(/\[(.*?)\][\[\(].*?[\]\)]/g, '$1')
  // Remove blockquotes
  .replace(/^\s{0,3}>\s?/g, '')
  // Remove reference-style links?
  .replace(/^\s{1,2}\[(.*?)\]: (\S+)( ".*?")?\s*$/g, '')
  // Remove atx-style headers
  .replace(/^(\n)?\s{0,}#{1,6}\s+| {0,}(\n)?\s{0,}#{0,} {0,}(\n)?\s{0,}$/gm, '$1$2$3')
  // Remove emphasis (repeat the line to remove double emphasis)
  .replace(/([\*_]{1,3})(\S.*?\S{0,1})\1/g, '$2')
  .replace(/([\*_]{1,3})(\S.*?\S{0,1})\1/g, '$2')
  // Remove code blocks
  .replace(/(`{3,})(.*?)\1/gm, '$2')
  // Remove inline code
  .replace(/`(.+?)`/g, '$1')
  // Replace two or more newlines with exactly two? Not entirely sure this belongs here...
  .replace(/\n{2,}/g, '\n\n');

  return text;
}

/**
 * **import { convertLinksToHyloRouterLinks } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Searches the content of a html element for link tags which are not already wrapped by
 * an ion-router-link or hylo-router-link component and does not have a target attribute
 * or with target="_self".
 * If the link is local, it will be connected to the hylo-router (which has to be present in your app)
 * to prevent a new page load and rather initiate a page change by the router.
 *
 * @param parent  the parent element to check its content for links
 */
export function convertLinksToHyloRouterLinks(parent: HTMLElement) {

  const router:HTMLHyloRouterElement = document.querySelector('hylo-router');
  if (!router) return;

  const links = parent.querySelectorAll('a');

  for (let i=0, l=links.length; i<l; i++) {

    if (links[i].closest('ion-router, hylo-router')) continue;

    const target = links[i].getAttribute('target');
    if (target && target != '_self') continue;

    let href = links[i].getAttribute('href');
    let check = window.location.protocol+'//'+window.location.hostname;
    const port = window.location.port;
    if (port) check += ':'+port;

    if (href.indexOf('://') > 0) {
      if (href.indexOf(check) != 0) continue;
      href = href.substr(check.length);
      if (!href) href = '/';
    }

    links[i].setAttribute('href', href);

    links[i].addEventListener('click', (e: MouseEvent) => {

      e.stopImmediatePropagation();
      e.preventDefault();

      router.push((e.target as HTMLElement).getAttribute('href'));
    });
  }
}

/**
 * **import { slugify } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Slugifies a string.
 *
 * Thanks to https://medium.com/@mhagemann/the-ultimate-way-to-slugify-a-url-string-in-javascript-b8e4a0d849e1
 *
 * @param string  the string to slugify
 */
export function slugify(string: string): string {
  const a = 'àáâäæãåāăąçćčđďèéêëēėęěğǵḧîïíīįìłḿñńǹňôöòóœøōõṕŕřßśšşșťțûüùúūǘůűųẃẍÿýžźż·/_,:;'
  const b = 'aaaaaaaaaacccddeeeeeeeegghiiiiiilmnnnnooooooooprrsssssttuuuuuuuuuwxyyzzz------'
  const p = new RegExp(a.split('').join('|'), 'g')

  return string.toString().toLowerCase()
    .replace(/\s+/g, '-') // Replace spaces with -
    .replace(p, c => b.charAt(a.indexOf(c))) // Replace special characters
    .replace(/&/g, '-and-') // Replace & with 'and'
    .replace(/[^\w\-]+/g, '') // Remove all non-word characters
    .replace(/\-\-+/g, '-') // Replace multiple - with single -
    .replace(/^-+/, '') // Trim - from start of text
    .replace(/-+$/, '') // Trim - from end of text
}

/**
 * **import { textToHtml } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Converts a text into html markup.
 * Adds paragraphs for text blocks which are separated by two line breaks
 * and adds links for text passages starting with "http" or "https".
 *
 * Escapes special Html characters.
 *
 * @param text  the text to be converted
 * @param br    add <br/> for single line breaks
 */
export function textToHtml(text: string, br?:boolean): any {

  text = escapeHtml(text);
  text = linkify(text);

  const splits = text.split("\n");

  let p = true;
  for (let i=0; i<splits.length; i++) {
    if (splits[i].trim() == "") {
      splits[i] = "</p><p>";
      p = true;
    } else if (!p && br) {
      splits.splice(i, 0, "<br/>");
      i++;
    } else {
      p = false;
    }
  }

  splits.unshift('<p>');
  splits.push('</p>');

  text = splits.join('');

  return text;
}

/**
 * **import { escapeHtml } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Escapes special characters.
 *
 * &amp; -> &amp;amp;<br/>
 * &lt; -> &amp;lt;<br/>
 * &gt; -> &amp;gt;<br/>
 * " -> &amp;quot;<br/>
 * ' -> &amp;#039;
 *
 * @param text  the text to be escaped
 */
export function escapeHtml(text: string): string {
  var map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  };

  return text.replace(/[&<>"']/g, function(m) { return map[m]; });
}

/**
 * **import { escapeHtmlNamed } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Escapes named special characters like &euro;. 
 * It uses the default entities list of TinyMCE.
 *
 * @param text  the text to be escaped
 */
export function escapeHtmlNamed(text: string): string {

  const entities  = '38,amp,60,lt,62,gt,' +
  '160,nbsp,161,iexcl,162,cent,163,pound,164,curren,165,yen,166,brvbar,' +
  '167,sect,168,uml,169,copy,170,ordf,171,laquo,172,not,173,shy,174,' +
  'reg,175,macr,176,deg,177,plusmn,178,sup2,179,sup3,180,acute,181,' +
  'micro,182,para,183,middot,184,cedil,185,sup1,186,ordm,187,raquo,188,' +
  'frac14,189,frac12,190,frac34,191,iquest,192,Agrave,193,Aacute,194,' +
  'Acirc,195,Atilde,196,Auml,197,Aring,198,AElig,199,Ccedil,200,Egrave,' +
  '201,Eacute,202,Ecirc,203,Euml,204,Igrave,205,Iacute,206,Icirc,207,' +
  'Iuml,208,ETH,209,Ntilde,210,Ograve,211,Oacute,212,Ocirc,213,Otilde,' +
  '214,Ouml,215,times,216,Oslash,217,Ugrave,218,Uacute,219,Ucirc,220,' +
  'Uuml,221,Yacute,222,THORN,223,szlig,224,agrave,225,aacute,226,acirc,' +
  '227,atilde,228,auml,229,aring,230,aelig,231,ccedil,232,egrave,233,' +
  'eacute,234,ecirc,235,euml,236,igrave,237,iacute,238,icirc,239,iuml,' +
  '240,eth,241,ntilde,242,ograve,243,oacute,244,ocirc,245,otilde,246,' +
  'ouml,247,divide,248,oslash,249,ugrave,250,uacute,251,ucirc,252,uuml,' +
  '253,yacute,254,thorn,255,yuml,402,fnof,913,Alpha,914,Beta,915,Gamma,' +
  '916,Delta,917,Epsilon,918,Zeta,919,Eta,920,Theta,921,Iota,922,Kappa,' +
  '923,Lambda,924,Mu,925,Nu,926,Xi,927,Omicron,928,Pi,929,Rho,931,' +
  'Sigma,932,Tau,933,Upsilon,934,Phi,935,Chi,936,Psi,937,Omega,945,' +
  'alpha,946,beta,947,gamma,948,delta,949,epsilon,950,zeta,951,eta,952,' +
  'theta,953,iota,954,kappa,955,lambda,956,mu,957,nu,958,xi,959,' +
  'omicron,960,pi,961,rho,962,sigmaf,963,sigma,964,tau,965,upsilon,966,' +
  'phi,967,chi,968,psi,969,omega,977,thetasym,978,upsih,982,piv,8226,' +
  'bull,8230,hellip,8242,prime,8243,Prime,8254,oline,8260,frasl,8472,' +
  'weierp,8465,image,8476,real,8482,trade,8501,alefsym,8592,larr,8593,' +
  'uarr,8594,rarr,8595,darr,8596,harr,8629,crarr,8656,lArr,8657,uArr,' +
  '8658,rArr,8659,dArr,8660,hArr,8704,forall,8706,part,8707,exist,8709,' +
  'empty,8711,nabla,8712,isin,8713,notin,8715,ni,8719,prod,8721,sum,' +
  '8722,minus,8727,lowast,8730,radic,8733,prop,8734,infin,8736,ang,' +
  '8743,and,8744,or,8745,cap,8746,cup,8747,int,8756,there4,8764,sim,' +
  '8773,cong,8776,asymp,8800,ne,8801,equiv,8804,le,8805,ge,8834,sub,' +
  '8835,sup,8836,nsub,8838,sube,8839,supe,8853,oplus,8855,otimes,8869,' +
  'perp,8901,sdot,8968,lceil,8969,rceil,8970,lfloor,8971,rfloor,9001,' +
  'lang,9002,rang,9674,loz,9824,spades,9827,clubs,9829,hearts,9830,' +
  'diams,338,OElig,339,oelig,352,Scaron,353,scaron,376,Yuml,710,circ,' +
  '732,tilde,8194,ensp,8195,emsp,8201,thinsp,8204,zwnj,8205,zwj,8206,' +
  'lrm,8207,rlm,8211,ndash,8212,mdash,8216,lsquo,8217,rsquo,8218,sbquo,' +
  '8220,ldquo,8221,rdquo,8222,bdquo,8224,dagger,8225,Dagger,8240,' +
  'permil,8249,lsaquo,8250,rsaquo,8364,euro';

  const dict = {};
  const list = entities.split(',');
  for (let i=0, l=list.length; i<l; i += 2) dict['_'+list[i]] = '&'+list[i+1]+';';

  for (let i=0; i<text.length; i++) {
    const code = '_'+text.charCodeAt(i);
    if (dict[code]) {
      text = text.substring(0,i)+dict[code]+text.substring(i+1);
      i += dict[code].length-1;
    }
  }

  return text;
}

/**
 * **import { utf8Encode } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Encodes multi-byte Unicode string into utf-8 multiple single-byte characters
 * (BMP / basic multilingual plane only).
 *
 * Chars in range U+0080 - U+07FF are encoded in 2 chars, U+0800 - U+FFFF in 3 chars.
 *
 * Can be achieved in JavaScript by unescape(encodeURIComponent(str)),
 * but this approach may be useful in other languages.
 *
 * @param   unicodeString - Unicode string to be encoded as UTF-8
 */
export function utf8Encode(unicodeString: string): string {
  if (typeof unicodeString != 'string') throw new TypeError('parameter ‘unicodeString’ is not a string');
  const utf8String = unicodeString.replace(
    /[\u0080-\u07ff]/g,  // U+0080 - U+07FF => 2 bytes 110yyyyy, 10zzzzzz
    function(c) {
      var cc = c.charCodeAt(0);
      return String.fromCharCode(0xc0 | cc>>6, 0x80 | cc&0x3f);
    }
  ).replace(
    /[\u0800-\uffff]/g,  // U+0800 - U+FFFF => 3 bytes 1110xxxx, 10yyyyyy, 10zzzzzz
    function(c) {
      var cc = c.charCodeAt(0);
      return String.fromCharCode(0xe0 | cc>>12, 0x80 | cc>>6&0x3F, 0x80 | cc&0x3f);
    }
  );
  return utf8String;
}

/**
 * **import { utf8Decode } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Decodes utf-8 encoded string back into multi-byte Unicode characters.
 *
 * Can be achieved in JavaScript by decodeURIComponent(escape(str)),
 * but this approach may be useful in other languages.
 *
 * @param   utf8String  UTF-8 string to be decoded back to Unicode
 */
export function utf8Decode(utf8String: string): string {
  if (typeof utf8String != 'string') throw new TypeError('parameter ‘utf8String’ is not a string');
  // note: decode 3-byte chars first as decoded 2-byte strings could appear to be 3-byte char!
  const unicodeString = utf8String.replace(
    /[\u00e0-\u00ef][\u0080-\u00bf][\u0080-\u00bf]/g,  // 3-byte chars
    function(c) {  // (note parentheses for precedence)
      var cc = ((c.charCodeAt(0)&0x0f)<<12) | ((c.charCodeAt(1)&0x3f)<<6) | ( c.charCodeAt(2)&0x3f);
      return String.fromCharCode(cc);
    }
  ).replace(
    /[\u00c0-\u00df][\u0080-\u00bf]/g,                 // 2-byte chars
    function(c) {  // (note parentheses for precedence)
      var cc = (c.charCodeAt(0)&0x1f)<<6 | c.charCodeAt(1)&0x3f;
      return String.fromCharCode(cc);
    }
  );
  return unicodeString;
}

/**
 * **import { unicodeDecode } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Decodes a string which contains unicode characters (\uXXXX).
 *
 * @param  unicodeString   unicode string
 */
export function unicodeDecode(unicodeString: string): string {
  if (!unicodeString) return unicodeString;
  return unicodeString.replace(/\\u[\dA-F]{4}/gi,
    function (match) {
      return String.fromCharCode(parseInt(match.replace(/\\u/g, ''), 16));
    });
}

/**
 * **import { linkify } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Converts all links in a text to clickable html links.
 * Finds http, https, ftp and file.
 *
 * @param text  the text to convert
 */
export function linkify(text: string): string {
  const reUrl = /(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gi

  return text.replace(reUrl, url => `<a href="${url}" target="_blank">${url}</a>`)
}

/**
 * **import { cleanPhoneNumber } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Removes all characters which are not numeric or "+".
 * Removes local 0 if a country code with "+" is provided for most of europes countries.
 *
 * @param phone_number  the number to be cleaned
 */
export function cleanPhoneNumber(phone_number: string): string {

  if (!phone_number) return phone_number;

  for (let i=0; i<phone_number.length; i++) {
    if (isNaN(phone_number[i] as unknown as number) && phone_number[i] != "+" || phone_number[i] == " ") {

      phone_number = phone_number.substr(0, i) + phone_number.substr(i+1);

      if (phone_number.indexOf('+300') === 0) phone_number = '+30' + phone_number.substr(4);
      else if (phone_number.indexOf('+310') === 0) phone_number = '+31' + phone_number.substr(4);
      else if (phone_number.indexOf('+320') === 0) phone_number = '+32' + phone_number.substr(4);
      else if (phone_number.indexOf('+330') === 0) phone_number = '+33' + phone_number.substr(4);
      else if (phone_number.indexOf('+3530') === 0) phone_number = '+353' + phone_number.substr(5);
      else if (phone_number.indexOf('+3550') === 0) phone_number = '+355' + phone_number.substr(5);
      else if (phone_number.indexOf('+3580') === 0) phone_number = '+358' + phone_number.substr(5);
      else if (phone_number.indexOf('+3590') === 0) phone_number = '+359' + phone_number.substr(5);
      else if (phone_number.indexOf('+366') === 0) phone_number = '+36' + phone_number.substr(4);
      else if (phone_number.indexOf('+3700') === 0) phone_number = '+370' + phone_number.substr(5);
      else if (phone_number.indexOf('+3730') === 0) phone_number = '+373' + phone_number.substr(5);
      else if (phone_number.indexOf('+3740') === 0) phone_number = '+374' + phone_number.substr(5);
      else if (phone_number.indexOf('+3758') === 0) phone_number = '+375' + phone_number.substr(5);
      else if (phone_number.indexOf('+3800') === 0) phone_number = '+380' + phone_number.substr(5);
      else if (phone_number.indexOf('+3810') === 0) phone_number = '+381' + phone_number.substr(5);
      else if (phone_number.indexOf('+3820') === 0) phone_number = '+382' + phone_number.substr(5);
      else if (phone_number.indexOf('+3850') === 0) phone_number = '+385' + phone_number.substr(5);
      else if (phone_number.indexOf('+3860') === 0) phone_number = '+386' + phone_number.substr(5);
      else if (phone_number.indexOf('+3870') === 0) phone_number = '+387' + phone_number.substr(5);
      else if (phone_number.indexOf('+3890') === 0) phone_number = '+389' + phone_number.substr(5);
      else if (phone_number.indexOf('+400') === 0) phone_number = '+40' + phone_number.substr(4);
      else if (phone_number.indexOf('+410') === 0) phone_number = '+41' + phone_number.substr(4);
      else if (phone_number.indexOf('+4210') === 0) phone_number = '+421' + phone_number.substr(5);
      else if (phone_number.indexOf('+430') === 0) phone_number = '+43' + phone_number.substr(4);
      else if (phone_number.indexOf('+440') === 0) phone_number = '+44' + phone_number.substr(4);
      else if (phone_number.indexOf('+460') === 0) phone_number = '+46' + phone_number.substr(4);
      else if (phone_number.indexOf('+480') === 0) phone_number = '+48' + phone_number.substr(4);
      else if (phone_number.indexOf('+490') === 0) phone_number = '+49' + phone_number.substr(4);

      i--;
    }
  }

  return phone_number;
}

/**
 * **import { getRange } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Converts a range string into a an array of the values of the range.
 * A range string consists of the starting value of the range followed by ".." and the end value.
 * The start and end value can be either any number or a character each.
 * If they are a character each, the character codes are used to build the range.
 * If they are numbers, the decimal places define the steps of the range build.
 *
 * E.g.: 1..10 or A..Z or 0.0..2 (which would use 0.1 steps)
 *
 * If "values" does not define a range, an array with "values" as its only value is returned.
 *
 * @param values    the range as string (if not a string, returns null)
 * @param skip      entries to skip when building the range (default: 0)
 */
export function getRange(values: string, skip: number = 0): any[] {

  if (typeof values !== 'string') return null;

  const results = [];
  skip = Number.parseInt(skip+'');
  if (Number.isNaN(skip) || skip < 0) skip = 0;


  const split = values.split('..');

  if (split.length != 2) {
    results.push(values.trim());
    return results;
  }

  const first = split[0].trim();
  const last = split[1].trim();
  if (first == '' || last == '') {
    results.push(values.trim());
    return results;
  }


  let float = false;
  let integer = Number.parseInt(first)+'' == first && Number.parseInt(last)+'' == last;
  if (!integer && first.charAt(first.length-1) != '.' && last.charAt(last.length-1) != '.') {
    let first_zero = first;
    let last_zero = last;
    while (first_zero.charAt(first_zero.length-1) == '0') first_zero = first_zero.substring(0, first_zero.length-1);
    while (last_zero.charAt(last_zero.length-1) == '0') last_zero = last_zero.substring(0, last_zero.length-1);
    if (first_zero.charAt(first_zero.length-1) == '.') first_zero = first_zero.substring(0, first_zero.length-1);
    if (last_zero.charAt(last_zero.length-1) == '.') last_zero = last_zero.substring(0, last_zero.length-1);
    float = Number.parseFloat(first)+'' == first_zero && Number.parseFloat(last)+'' == last_zero;
  }


  let step = 1;
  let round = 1;
  if (float) {
    const first_split = first.split('.');
    const last_split = last.split('.');
    first_split.push('');
    last_split.push('');
    round = Math.pow(10, Math.max(first_split[1].length, last_split[1].length));
    step = 1 / round;
  }

  let start = first.charCodeAt(0);
  let end = last.charCodeAt(0);

  if (integer) {
    start = Number.parseInt(first);
    end = Number.parseInt(last);
    if (Number.isNaN(start) || Number.isNaN(end)) {
      results.push(values.trim());
      return results;
    }
  } else if (float) {
    start = Number.parseFloat(first);
    end = Number.parseFloat(last);
    if (Number.isNaN(start) || Number.isNaN(end)) {
      results.push(values.trim());
      return results;
    }
  } else if (first.length > 1 || last.length > 1) {
    results.push(values.trim());
    return results;
  }


  if (start > end) step *= -1;

  let skip_counter = 0;
  for (; step > 0 ? start<=end : start>=end; start+=step) {

    start = Math.round(start*round) / round;

    if (skip_counter == 0) skip_counter = skip;
    else {
      skip_counter--;
      continue;
    }

    if (integer || float) results.push(start);
    else results.push(String.fromCharCode(start));
  }

  return results;
}

/**
 * **import { getFromLocationSearch } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Returns the first value of the key from the url search string.
 *
 * @param key   the key for the value to return
 */
export function getFromLocationSearch(key: string): string {

  let params = (new URL(window.location as unknown as string)).searchParams;

  return params.get(key);
}

/**
 * **import { getAllFromLocationSearch } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Returns all values of the key from the url search string.
 *
 * @param key   the key for the value to return
 */
export function getAllFromLocationSearch(key: string): string[] {

  let params = (new URL(window.location as unknown as string)).searchParams;

  return params.getAll(key);
}

/**
 * **import { getQueryFromLocationSearch } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Returns an object with all url search parameters as key, value pairs
 * or, if URLSearchParams is set, as URLSearchParams object.
 *
 * @param URLSearchParams   return URLSearchParams object
 */
export function getQueryFromLocationSearch(URLSearchParams?: boolean): any {

  let params = (new URL(window.location as unknown as string)).searchParams as any;
  if (URLSearchParams) return params;

  let query: any = {};

  for (var pair of params.entries()) {
    query[pair[0]] = pair[1];
  }

  return query;
}

/**
 * **import { hasLocationSearchKey } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Checks if a certain value is set in the url search string.
 *
 * @params key  the key to be checked
 */
export function hasLocationSearchKey(key: string): boolean {

  let params = (new URL(window.location as unknown as string)).searchParams;

  return params.has(key);
}

/**
 * **import { addToLocationSearch } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Adds values to the url search string.
 * Replaces values if the key already exist.
 *
 * Fires the event "hyloLocationSearchChanged" on the windows object.
 *
 * @param values  an object with key/value pairs to add to the search string
 * @param replace whether to create a new history entry or replace the current
 */
export function addToLocationSearch(values: {[key: string]: string}, replace?: boolean) {

  let params = (new URL(window.location as unknown as string)).searchParams;

  for (let key in values) {
    params.set(key, values[key]);
  }

  const search =  params.toString() ? '?'+ params.toString() : '';
  if (replace) window.history.replaceState(window.history.state, document.title, window.location.pathname + search + window.location.hash);
  else window.history.pushState({}, document.title, window.location.pathname + search + window.location.hash);

  window.dispatchEvent(new Event('hyloLocationSearchChanged'));
}

/**
 * **import { appendToLocationSearch } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Appends key/value pairs to the url search string.
 * Always appends the key/value pair even if the key already exists.
 *
 * Fires the event "hyloLocationSearchChanged" on the windows object.
 *
 * @param values  an object with key/value pairs to append to the search string
 * @param replace whether to create a new history entry or replace the current
 */
export function appendToLocationSearch(values: {[key: string]: string}, replace?: boolean) {

  let params = (new URL(window.location as unknown as string)).searchParams;

  for (let key in values) {
    params.append(key, values[key]);
  }

  const search =  params.toString() ? '?'+ params.toString() : '';
  if (replace) window.history.replaceState(window.history.state, document.title, window.location.pathname + search + window.location.hash);
  else window.history.pushState({}, document.title, window.location.pathname + search + window.location.hash);

  window.dispatchEvent(new Event('hyloLocationSearchChanged'));
}

/**
 * **import { replaceLocationSearch } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Replaces an entire location search string with a new one.
 *
 * Fires the event "hyloLocationSearchChanged" on the windows object.
 *
 * @param values  an object with key/value pairs or a URLSearchParams object to append to the search string
 * @param replace whether to create a new history entry or replace the current
 */
export function replaceLocationSearch(values: {[key: string]: string} | URLSearchParams, replace?: boolean) {

  //console.log('replaceLocationSearch');

  let params: URLSearchParams;

  if (values instanceof URLSearchParams) params = values;
  else {
    params = new URLSearchParams();
    for (let key in values) params.set(key, values[key]);
  }

  const search =  params.toString() ? '?'+ params.toString() : '';
  if (replace) window.history.replaceState(window.history.state, document.title, window.location.pathname + search + window.location.hash);
  else window.history.pushState({}, document.title, window.location.pathname + search + window.location.hash);

  window.dispatchEvent(new Event('hyloLocationSearchChanged'));
}

/**
 * **import { removeFromLocationSearch } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Removes values from the url search string.
 *
 * Fires the event "hyloLocationSearchChanged" on the windows object.
 *
 * @param keys    an array of keys to be removed
 * @param replace whether to create a new history entry or replace the current
 */
export function removeFromLocationSearch(keys: string[], replace?: boolean) {

  //console.log('removeFromLocationSearch');

  let params = (new URL(window.location as unknown as string)).searchParams;

  for (let i=0, l=keys.length; i<l; i++) {
    params.delete(keys[i]);
  }

  const search =  params.toString() ? '?'+ params.toString() : '';
  if (replace) window.history.replaceState(window.history.state, document.title, window.location.pathname + search + window.location.hash);
  else window.history.pushState({}, document.title, window.location.pathname + search + window.location.hash);

  window.dispatchEvent(new Event('hyloLocationSearchChanged'));
}

/**
 * **import { checkActivePath } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Checks whether the given href is part of the current url.
 *
 * @param href        a string which is checked against window.location.href
 * @param exact       check not just for partial match but exact match
 * @param query       takes the query string into account
 * @param exact_query if set, it is not enough that the href query parameters are a subset of the URL query parameters
 */
export function checkActivePath(href: string, exact: boolean = false, query: boolean = false, exact_query: boolean = false) {

  if (!href || !href.trim()) return false;
  href = href.trim();

  if (query) {

    const location_params = (new URL(window.location as unknown as string)).searchParams;
    const href_params = (new URL(href, window.location as unknown as string)).searchParams;

    for (const [key, value] of (href_params as unknown as string[])) {
      if (!location_params.has(key) || !location_params.getAll(key).includes(value)) return false;
    }

    if (exact_query) {
      for (const [key, value] of (location_params as unknown as string[])) {
        if (!href_params.has(key) || !href_params.getAll(key).includes(value)) return false;
      }
    }
  }

  href = href.split('?')[0].split('#')[0];
  if (href[href.length-1] == "/") href = href.substring(0, href.length-1);
  const location = window.location.href.split('?')[0].split('#')[0];

  let first = -1;
  if ((first = location.indexOf(href)) >= 0) {

    if (!exact) return true;

    const last = location.lastIndexOf(href);
    if (last == first && Math.abs(last + href.length - location.length) <= 1 ) return true;
  }

  return false;
}

/**
 * **import { getLngLat } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Gathers the longitude and latitude of a given address string.
 *
 * The viewbox is the preferred area to find search results.
 * Any two corner points of the box are accepted as long as they span a real box. x is longitude, y is latitude.
 *
 * Uses https://nominatim.org/release-docs/develop/
 *
 * @param address the address to be searched for, separate the parts of the address by comma when possible
 * @param viewbox '<x1>,<y1>,<x2>,<y2>' (optional)
 * @return [longitude, latitude]
*/
export async function getLngLat(address: string, viewbox?: string): Promise<[number, number]> {

  const search = new URLSearchParams();
  search.append('format', 'json');
  search.append('limit', '1');
  search.append('q', address);

  if (viewbox) search.append('viewbox', viewbox);

  const result = await fetch('https://nominatim.openstreetmap.org/search?'+search.toString(), {
    method: 'GET',
  });
  const data = await result.json();

  if (!data.length) return null;

  return [Number.parseFloat(data[0].lon), Number.parseFloat(data[0].lat)];
}

/**
 * **import { geoDistance } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Calculates the distance between two geolocation points.
 *
 * @param start_point   [longitude, latitude]
 * @param end_point     [longitude, latitude]
 */
export function geoDistance(start_point: [number, number], end_point: [number, number]): number {

  const toRadians = function(value: number) { return value * Math.PI / 180; };

  const r = 6371e3; // earth radius in metres
  const lat_1 = toRadians(start_point[1]);
  const lat_2 = toRadians(end_point[1]);
  const lat_d = toRadians(end_point[1]-start_point[1]);
  const lon_d = toRadians(end_point[0]-start_point[0]);

  const a = Math.sin(lat_d/2) * Math.sin(lat_d/2) +
          Math.cos(lat_1) * Math.cos(lat_2) *
          Math.sin(lon_d/2) * Math.sin(lon_d/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

  return r * c;
}

/**
 * **import { getTimezone } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Returns the timezone of the local machine at winter (e.g. '+01:00').
 * You can submit a reference date which will be used to get the timezone.
 * To get the timezone of a summer time submit a date like ... new Date(2000, 6, 1).
 *
 * @param reference   a reference Date object
 * @param no_colon    removes the colon from the output (e.g. '+0100')
 */
export function getTimezone(reference?: Date, no_colon?: boolean): string {

  if (!reference) reference = new Date(2000, 0, 1);

  // Use minute-precise math to avoid floating point and handle half/quarter-hour offsets.
  const totalMinutes = reference.getTimezoneOffset(); // minutes between UTC and local (positive if local is behind UTC)
  const sign = totalMinutes > 0 ? '-' : '+';
  const absMinutes = Math.abs(totalMinutes);
  const hours = Math.floor(absMinutes / 60);
  const minutes = absMinutes % 60;

  const hh = (hours + '').padStart(2, '0');
  const mm = (minutes + '').padStart(2, '0');

  return sign + hh + (no_colon ? '' : ':') + mm;
}

/**
 * Small demonstration helper for getTimezone.
 * Returns example offsets for a winter and a summer reference date on the current machine.
 */
export function demo_getTimezone(): string[] {
  const winter = new Date(2000, 0, 1); // Jan 1, 2000 (winter time)
  const summer = new Date(2000, 6, 1); // Jul 1, 2000 (summer time)
  return [getTimezone(winter), getTimezone(summer)];
}

/**
 * **import { datetimeToTimezoneString } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Returns a datetime string in ISO format which can be safely parsed back into a Date object again.
 * The string contains the timezone of the local machine if the timezone is not given.
 *
 * The date and time can be supplied as one string in the first and second parameter respectively
 * instead of supplying each value separately.
 *
 * @param year      e.g. 2019 or 2019-08-05
 * @param month     e.g. 8 or 08 for august (starts counting at 1) or 03:02:09
 * @param day       e.g. 5 or 05
 * @param hours     e.g. 3 or 03
 * @param minutes   e.g. 2 or 02
 * @param seconds   e.g. 9 or 09
 * @param timezone  e.g. +01:00
 * @return 2019-08-05T03:02:09+01:00
 */
export function datetimeToTimezoneString(year:string | number, month?:string | number, day?:string | number, hours?:string | number, minutes?:string | number, seconds?:string | number, timezone?:string): string {

  year += '';

  year = year as string;

  if (!day && !hours && !minutes && !seconds) {

    if (!month) month = '00:00:00';
    month = month as string;

    const date = year.split('-');
    if (date.length < 2) date[1] = '01';
    if (date.length < 3) date[2] = '01';

    const time = month.split(':');
    if (time.length < 2) time[1] = '00';
    if (time.length < 3) time[2] = '00';

    year = date[0];
    month = date[1];
    day = date[2];
    hours = time[0];
    minutes = time[1];
    seconds = time[2];

  } else {

    if (!month) month = 1;
    if (!day) day = 1;
    if (!hours) hours = 0;
    if (!minutes) minutes = 0;
    if (!seconds) seconds = 0;

    month += '';
    day += '';
    hours += '';
    minutes += '';
    seconds += '';

    month = month as string;
    day = day as string;
    hours = hours as string;
    minutes = minutes as string;
    seconds = seconds as string;
  }

  if (!timezone) {
    const reference = new Date(parseInt(year), parseInt(month)-1, parseInt(day), parseInt(hours), parseInt(minutes), parseInt(seconds));
    timezone = getTimezone(reference);
  }

  if (month.length < 2) month = '0'+month;
  if (day.length < 2) day = '0'+day;
  if (hours.length < 2) hours = '0'+hours;
  if (minutes.length < 2) minutes = '0'+minutes;
  if (seconds.length < 2) seconds = '0'+seconds;

  return year+'-'+month+'-'+day+'T'+hours+':'+minutes+':'+seconds+timezone;
}

/**
 * **import { adjustTimezone } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Adjusts/adds the timezone in the given ISO formatted datetime string with the local machines timezone.
 * The date and time values stay the same so that it can be used for local string formatting.
 *
 * If the given datetime string has a timezone, the adjusted timezone will show a colon or not
 * depending whether the original showed one. (default is none)
 *
 * @param datetime  e.g. 2019-11-05T03:02:09+0200
 * @return 2019-11-05T03:02:09+0100
 */
export function adjustTimezone(datetime: string) {

  if (!datetime || datetime.indexOf('T') < 0) return datetime;

  const split = datetime.split('T');

  let colon = false;
  if (split[1].indexOf('+') > 0) {
    const temp = split[1].split('+');
    colon = temp[1].indexOf(':') > 0;
    split[1] = split[1].substr(0, split[1].indexOf('+'));
  } else if (split[1].indexOf('-') > 0) {
    const temp = split[1].split('-');
    colon = temp[1].indexOf(':') > 0;
    split[1] = split[1].substr(0, split[1].indexOf('-'));
  }

  const date = new Date(Date.parse(datetime));

  return split.join('T')+getTimezone(date, !colon);
}

/**
 * Demonstrates adjustTimezone behavior by keeping wall-clock time and relabeling offset to the machine's zone.
 */
export function demo_adjustTimezone(): { input: string, adjusted: string } {
  const input = '2019-11-05T03:02:09+0200';
  return { input, adjusted: adjustTimezone(input) };
}

/**
 * Adds/sets the timezone on a wall-time datetime string.
 *
 * Contract:
 * - datetime: required ISO string (YYYY-MM-DDTHH:mm[:ss][±HH[:]MM|Z]). Offset in the input is ignored for wall time.
 * - reference: optional ISO string; used for colon style and the reference-offset adjustment mode when timeZone is not provided.
 * - timeZone: optional IANA zone name; when provided, it determines the offset (DST-aware) for the given wall time.
 * - if reference and timeZone is not provided, timeZone defaults to "Europe/Berlin"
 *
 * Rules:
 * 1) Colon style: defaults to false. If datetime has tz with colon ⇒ true. Else if reference has tz with colon ⇒ true.
 * 2) Only reference provided: use local (machine) DST rules to decide if datetime and reference wall times are both in
 *    "winter" or both in "summer". Start with the reference's numeric offset; if the local periods differ, adjust the
 *    reference offset by ±(summerLocalOffset − winterLocalOffset) so it matches datetime's period. Then apply that
 *    adjusted reference offset to datetime's wall time (replace or add tz).
 *    If reference lacks a timezone token ⇒ console.warn and return datetime unchanged.
 * 3) Only timeZone provided: compute offset using getTimeZoneOffsetForWallTime(timeZone, wallTime) and apply it.
 * 4) Both provided: compute offset using timeZone (as in 3). Additionally validate: strip tz from reference, compute the
 *    offset in timeZone for that reference wall time; if it differs from reference's tz, console.warn.
 *
 * This function does not convert an instant; it labels the given wall time with an offset.
 */
export function setTimezone(datetime: string, reference?: string, timeZone?: string) {

    if (!datetime || datetime.indexOf('T') < 0) return datetime;

    if (!reference && !timeZone) timeZone = "Europe/Berlin";

    // Parse date/time parts from the input, ignoring any timezone present.
    const [datePart, timeAndZone] = datetime.split('T');
    if (!datePart || !timeAndZone) return datetime;

    // Detect if original timezone showed a colon (to preserve formatting style)
    let colon = false;
    const tzMatch = timeAndZone.match(/(Z|[+-]\d{2}:?\d{2})$/);
    if (tzMatch) colon = tzMatch[0].indexOf(':') > -1;
    else if (reference && reference.indexOf('T') > -1) {
      const refTz = reference.match(/(Z|[+-]\d{2}:?\d{2})$/);
      if (refTz) colon = refTz[0].indexOf(':') > -1;
    }

    const timePart = tzMatch ? timeAndZone.slice(0, -tzMatch[0].length) : timeAndZone;

    const d = datePart.split('-').map(v => parseInt(v, 10));
    const t = timePart.split(':').map(v => parseInt(v, 10));

    const year = d[0];
    const month = d[1];
    const day = d[2];
    const hour = t[0] || 0;
    const minute = t[1] || 0;
    const second = t[2] || 0;

    let offsetStr: string;
    let offsetMinutes: number | null = null;

    if (timeZone && !reference) {
      // Compute the correct offset for the given wall time in the provided IANA time zone.
      offsetMinutes = getTimeZoneOffsetForWallTime(timeZone, year, month, day, hour, minute, second);
    } else if (!timeZone && reference) {
      // Only reference given: replace datetime's tz with (possibly adjusted) reference tz per local DST rules
      const refTzMatch = reference.match(/(Z|[+-]\d{2}:?\d{2})$/);
      if (!refTzMatch) {
        // eslint-disable-next-line no-console
        console.warn(`[setTimezone] reference "${reference}" has no timezone; returning datetime unchanged`);
        return datetime;
      }

      const refOffsetMinutes = parseOffsetToMinutes(refTzMatch[0]);

      // Build local Dates from wall times to classify local DST period
      const date_dt_local = new Date(year, month - 1, day, hour, minute, second, 0);
      const dtLocalMinutes = -date_dt_local.getTimezoneOffset();

      // Parse reference wall time (ignore its tz)
      const refNoTz = reference.slice(0, -refTzMatch[0].length);
      const [refDatePart, refWallTime] = refNoTz.split('T');
      const rd = refDatePart.split('-').map(v => parseInt(v, 10));
      const rt = (refWallTime || '00:00:00').split(':').map(v => parseInt(v, 10));
      const rYear = rd[0];
      const rMonth = rd[1];
      const rDay = rd[2];
      const rHour = rt[0] || 0;
      const rMinute = rt[1] || 0;
      const rSecond = rt[2] || 0;
      const date_ref_local = new Date(rYear, rMonth - 1, rDay, rHour, rMinute, rSecond, 0);
      const refLocalMinutes = -date_ref_local.getTimezoneOffset();

      // Determine canonical local winter/summer offsets for the target wall time year
      const winterLocalMinutes = -new Date(year, 0, 1, 12, 0, 0, 0).getTimezoneOffset();
      const summerLocalMinutes = -new Date(year, 6, 1, 12, 0, 0, 0).getTimezoneOffset();
      const dstDelta = summerLocalMinutes - winterLocalMinutes; // often +60 in DST locales

      const dtIsSummer = dtLocalMinutes === summerLocalMinutes;
      const refIsSummer = refLocalMinutes === summerLocalMinutes;

      // Compute adjusted offset from reference offset by local DST difference
      const adjust = (dtIsSummer ? 1 : 0) - (refIsSummer ? 1 : 0);
      offsetMinutes = refOffsetMinutes + adjust * dstDelta;

      // Validity checks (best-effort)
      const adjDiff = offsetMinutes - refOffsetMinutes;
      if (Math.abs(adjDiff) !== 0 && Math.abs(adjDiff) !== Math.abs(dstDelta)) {
        // eslint-disable-next-line no-console
        console.warn('[setTimezone] adjusted offset differs by more than local DST delta');
      }
      const abs = Math.abs(offsetMinutes);
      const mm = abs % 60;
      if (abs > 14 * 60 || mm % 15 !== 0) {
        // eslint-disable-next-line no-console
        console.warn('[setTimezone] produced offset is unusual/out-of-range');
      }
    } else if (timeZone && reference) {
      // Use timeZone as source of truth
      offsetMinutes = getTimeZoneOffsetForWallTime(timeZone, year, month, day, hour, minute, second);

      // Validate reference against timeZone if reference carries a tz
      const refTzMatch = reference.match(/(Z|[+-]\d{2}:?\d{2})$/);
      const refTimePart = refTzMatch ? reference.slice(0, -refTzMatch[0].length) : reference;
      const [refDatePart, refWallTime] = refTimePart.split('T');
      if (refDatePart && refWallTime) {
        const rd = refDatePart.split('-').map(v => parseInt(v, 10));
        const rt = refWallTime.split(':').map(v => parseInt(v, 10));
        const rYear = rd[0], rMonth = rd[1], rDay = rd[2];
        const rHour = rt[0] || 0, rMinute = rt[1] || 0, rSecond = rt[2] || 0;
        const zoneRefMinutes = getTimeZoneOffsetForWallTime(timeZone, rYear, rMonth, rDay, rHour, rMinute, rSecond);
        const refOffsetMinutes = refTzMatch ? parseOffsetToMinutes(refTzMatch[0]) : null;
        if (refOffsetMinutes !== null && zoneRefMinutes !== refOffsetMinutes) {
          // Best-effort warning for mismatch between provided reference tz and zone rules
          // eslint-disable-next-line no-console
          console.warn(`[setTimezone] reference timezone offset "${refTzMatch[0]}" does not match provided timeZone "${timeZone}" at the reference wall time`);
        }
      }
    }

    if (offsetMinutes === null) {
      // If neither reference nor timeZone was provided, return unchanged (contract says one is required)
      return datetime;
    }

    const sign = offsetMinutes >= 0 ? '+' : '-';
    const abs = Math.abs(offsetMinutes);
    const hh = String(Math.floor(abs / 60)).padStart(2, '0');
    const mm = String(abs % 60).padStart(2, '0');
    offsetStr = sign + hh + (colon ? ':' : '') + mm;

    return datePart + 'T' + timePart + offsetStr;
}

// Parse timezone offset token (e.g., Z, +01:00, +0100) into minutes (positive = ahead of UTC)
function parseOffsetToMinutes(tzToken: string): number {
  if (!tzToken || tzToken === 'Z') return 0;
  const m = tzToken.match(/^([+-])(\d{2}):?(\d{2})$/);
  if (!m) return 0;
  const sign = m[1] === '+' ? 1 : -1;
  const hh = parseInt(m[2], 10);
  const mm = parseInt(m[3], 10);
  return sign * (hh * 60 + mm);
}

/**
 * Resolve the UTC offset (in minutes) for a given local wall time in an IANA time zone.
 * This uses Intl.DateTimeFormat to iteratively find the UTC instant that renders the desired
 * local wall time in the specified zone, then derives the offset.
 */
function getTimeZoneOffsetForWallTime(timeZone: string, year: number, month: number, day: number, hour: number, minute: number, second: number): number {
  // Desired wall time encoded as a UTC epoch (treating local wall as if it were UTC)
  const targetUTC = Date.UTC(year, month - 1, day, hour, minute, second, 0);

  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  // For a given UTC instant, return the local wall time (in the target zone) encoded as a UTC epoch
  const partsToUTC = (instantUTCms: number) => {
    const parts = fmt.formatToParts(new Date(instantUTCms));
    const get = (type: string) => parseInt(parts.find(p => p.type === type)?.value || '0', 10);
    const y = get('year');
    const m = get('month');
    const da = get('day');
    const h = get('hour');
    const mi = get('minute');
    const s = get('second');
    return Date.UTC(y, m - 1, da, h, mi, s, 0);
  };

  // Single-pass correction: compute how the naive UTC instant renders in the target zone,
  // then adjust once to align the rendered local time with the desired wall time.
  const renderedLocalUTC = partsToUTC(targetUTC);
  const delta = targetUTC - renderedLocalUTC;
  const adjustedInstant = targetUTC + delta;

  // Offset (minutes) positive when zone is ahead of UTC (e.g., +60 for UTC+1)
  return Math.round((targetUTC - adjustedInstant) / 60000);
}

/**
 * Demonstrates setTimezone behavior by keeping wall time and applying the machine's DST-aware offset
 * for the date/time derived from the reference.
 */
export function demo_setTimezone(): { input: string, reference: string, result: string } {
  const input = '2019-11-05T03:02:09+0200';
  const reference = '2020-12-01T00:00:00+0100';
  return { input, reference, result: setTimezone(input, reference) };
}

/**
 * Demonstrates setTimezone with a named IANA zone (e.g., Europe/Berlin), ignoring the input's original tz.
 */
export function demo_setTimezone_namedZone(): { input: string, zone: string, result: string } {
  const input = '2019-11-05T03:02:09+0200';
  const zone = 'Europe/Berlin';
  return { input, zone, result: setTimezone(input, '2020-12-01T00:00:00+0100', zone) };
}

/**
 * Formats a given ISO 8601 date string according to specified or default locale and options.
 * The input is parsed to a moment in time and then formatted in the given time zone (default Europe/Berlin).
 * If the input includes an explicit offset/Z, that instant is used; otherwise, parsing uses JS Date rules
 * (date-only strings are treated as UTC midnight; datetime without offset is treated as local machine time).
 *
 * @param iso8601Date - The ISO 8601 date string to format.
 * @param locale - (Optional) A string with a BCP 47 language tag, or an array of such strings.
 *                 Default is 'de-DE'.
 * @param options - (Optional) An object that contains Intl.DateTimeFormat options.
 *                  Default format options are: { year: 'numeric', month: '2-digit', day: '2-digit' }
 * @returns A string representing the formatted date and time.
 *
 * @example
 * // Returns "Do., 23 Nov. 2023, 23:59"
 * formatDateWithLocale('2023-11-22T23:00:00+00:00')
 *
 * @example
 * // Returns "Thu, 11/23/2023, 11:59 PM"
 * formatDateWithLocale('2023-11-22T23:00:00+00:00', 'en-US', { weekday: 'short', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: true })
 */
export function formatDateWithLocale(iso8601Date: string, locale = 'de-DE', options: Intl.DateTimeFormatOptions = null, timezone: string = 'Europe/Berlin'): string {

  if (!iso8601Date) return null;

  const defaultOptions = {
    year: 'numeric' as const,
    month: '2-digit' as const,
    day: '2-digit' as const,
  };

  const formatterOptions = {
    ...(options || defaultOptions),
    timeZone: timezone,
  };

  const dateObject = new Date(iso8601Date);

  // Check if the date is valid
  if (isNaN(dateObject.getTime())) {
    return null; // FIXME: Throw an exception
  }

  const formatter = new Intl.DateTimeFormat(locale, formatterOptions);

  return formatter.format(dateObject);
}

/**
 * Demonstrates formatDateWithLocale conversions to Europe/Berlin using different locales/options.
 */
export function demo_formatDateWithLocale(): { de: string, en: string } {
  const iso = '2023-11-22T23:00:00+00:00';
  const de = formatDateWithLocale(iso);
  const en = formatDateWithLocale(
    iso,
    'en-US',
    { weekday: 'short', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: true }
  );
  return { de, en };
}

/**
 * Normalize one or two ISO date/time strings and report which parts match for display decisions.
 *
 * Behavior
 * - Time section defaults: If either input lacks a time section (no "T..."), "T12:00:00" is appended before processing.
 * - Timezone normalization (only when both datetime and reference are provided):
 *   - When respect_timezones = true: if exactly one input has a timezone token ("Z" or "+/-HH[:MM]"), copy it to the
 *     other so both share the same timezone; if both have or both lack a token, leave them as-is.
 *   - When respect_timezones = false: strip any timezone tokens from both so they are interpreted in the local machine
 *     timezone for comparison.
 * - Parsing: Builds Date objects from the normalized strings. On failure, logs a console warning and returns null.
 * - Flags:
 *   - timestamp: milliseconds since the Unix epoch for the normalized datetime (primary input).
 *   - year: false when no reference is given; otherwise true if years match.
 *   - month: false when no reference is given; otherwise true only if year is true and months match.
 *   - day: false when no reference is given; otherwise true only if month is true and calendar day matches.
 *   - time: evaluated only when day is true and a reference is given:
 *       • true only if both inputs had an explicit time section and hour+minute match;
 *       • false otherwise (including when either input lacks a time section).
 *
 * Examples
 * - normalizeDatetime('2025-06-01', undefined) → { timestamp, year:true, month:true, day:true, time:false }
 * - normalizeDatetime('2025-06-01T12:00:00', '2025-06-01T12:00:00') → { ..., year:true, month:true, day:true, time:true }
 * - normalizeDatetime('2025-06-01T12:00:00+02:00', '2025-06-01', true) → aligns tz so both comparable in the same zone.
 *
 * Note: This function does not convert instants between time zones; it normalizes strings for consistent comparison of
 * their calendar parts and optionally their wall-clock time (hour and minute).
 *
 * @param datetime           The primary ISO string (YYYY-MM-DD or YYYY-MM-DDTHH:mm[:ss][Z|±HH[:MM]]). Required.
 * @param reference          An optional ISO string to compare against. Same accepted forms as datetime.
 * @param respect_timezones  When true and both are provided, align timezone presence by copying the existing token to
 *                           the other input if only one has it; when false, remove timezone tokens from both.
 * @returns An object { timestamp, year, month, day, time } or null if parsing fails.
 */
export function normalizeDatetime(
  datetime: string,
  reference: string = null,
  respect_timezones: boolean = false
): { timestamp: number, day: boolean, month: boolean, year: boolean, time: boolean, notime: boolean } {

  if (!datetime) return null;

  // Helpers
  const tzRegex = /(Z|[+-]\d{2}:?\d{2})$/;
  const hasTimeSection = (s: string) => /T\d{2}:\d{2}/.test(s);
  const ensureTimeSection = (s: string) => hasTimeSection(s) ? s : (s + 'T12:00:00');
  const stripTimezoneToken = (s: string) => s.replace(tzRegex, '');
  const extractTimezoneToken = (s: string): string => {
    const m = s.match(tzRegex);
    return m ? m[0] : null;
  };

  // Record initial time-section presence BEFORE adding default time
  const dtHadTime = hasTimeSection(datetime);
  const refHadTime = reference ? hasTimeSection(reference) : false;

  // Add default time section where missing
  let dtStr = ensureTimeSection(datetime);
  let refStr = reference ? ensureTimeSection(reference) : null;

  // Align timezone presence depending on respect_timezones
  if (reference) {
    const dtTz = extractTimezoneToken(dtStr);
    const refTz = extractTimezoneToken(refStr);
    if (respect_timezones) {
      // If exactly one has tz, copy it to the other so both match
      if (dtTz && !refTz) refStr = refStr + dtTz;
      else if (!dtTz && refTz) dtStr = dtStr + refTz;
      // If both have or both lack tz, do nothing
    } else {
      // Remove timezone tokens so both are interpreted in local machine timezone
      dtStr = stripTimezoneToken(dtStr);
      refStr = stripTimezoneToken(refStr);
    }
  } else {
    // No reference provided:
    // - If respect_timezones is false, strip tz so it is interpreted in local machine timezone
    // - If respect_timezones is true, keep any existing tz on datetime
    if (!respect_timezones) {
      dtStr = stripTimezoneToken(dtStr);
    }
  }

  // Build Date objects for comparison
  const dt = new Date(dtStr);
  if (isNaN(dt.getTime())) {
    // eslint-disable-next-line no-console
    console.warn('[normalizeDatetime] Failed to parse normalized datetime:', dtStr);
    return null;
  }

  const result = {
    timestamp: dt.getTime(),
    year: false,
    month: false,
    day: false,
    time: false,
    // notime: true when primary input lacked an explicit time section
    notime: !dtHadTime,
  };

  if (!reference) {
    // No reference: all flags remain false per spec
    // year/month/day/time only become true when a reference is provided and values match
    return result;
  }

  const rf = new Date(refStr);
  if (isNaN(rf.getTime())) {
    // eslint-disable-next-line no-console
    console.warn('[normalizeDatetime] Failed to parse normalized reference:', refStr);
    return null;
  }

  // Compare year/month/date using local calendar fields
  const sameYear = dt.getFullYear() === rf.getFullYear();
  const sameMonth = sameYear && (dt.getMonth() === rf.getMonth());
  const sameDate = sameMonth && (dt.getDate() === rf.getDate());

  result.year = sameYear;
  result.month = sameMonth;
  result.day = sameDate;

  // Time rule (requires same Y/M/D): true only if both had time and hour+minute match
  if (sameDate) {
    result.time = (dtHadTime && refHadTime && (dt.getHours() === rf.getHours()) && (dt.getMinutes() === rf.getMinutes()));
  } else {
    result.time = false;
  }

  return result;
}

/**
 * Build a formatted string from a normalized datetime and a compact format template.
 *
 * Format template
 * - Conditional sections: [year ...], [month ...], [day ...], [time ...]
 *   - The inner content is included only if the corresponding boolean on the normalized object is true.
 *   - Negation is supported: [!year ...] includes when normalized.year is false (likewise for month/day/time).
 *   - Sections may be nested.
 * - Replacement tokens:
 *   - {year}    → formatted year
 *   - {month}   → formatted month
 *   - {day}     → day-of-month
 *   - {date}    → day + month combined (uses the same styles as {day} and {month})
 *   - {weekday} → localized weekday name
 *   - {hour}    → hour-of-day
 *   - {minute}  → minutes
 *   - {seconds} → seconds
 *   - {time}    → hour + minute combined
 *
 * Formatting options (Intl)
 * - The function uses Intl.DateTimeFormat under the hood and honors the following options if provided:
 *   - year:    'numeric' | '2-digit'                (default: 'numeric')
 *   - month:   'numeric' | '2-digit' | 'long' | 'short' | 'narrow' (default: 'short')
 *   - day:     'numeric' | '2-digit'                (default: 'numeric')
 *   - weekday: 'long' | 'short' | 'narrow'          (default: 'short')
 *   - hour:    'numeric' | '2-digit'                (default: 'numeric')
 *   - minute:  'numeric' | '2-digit'                (default: '2-digit')
 *   - second:  'numeric' | '2-digit'                (default: '2-digit')
 *   - timeZone, hour12, calendar, numberingSystem   (passed through when specified)
 *
 * Notes
 * - The date/time is taken from normalized.timestamp.
 * - Unknown tokens are left as-is (e.g., {foo}).
 *
 * @param normalized  Result from normalizeDatetime: { timestamp, year, month, day, time }.
 * @param format      The format template string.
 * @param options     Intl.DateTimeFormat options affecting individual token formatting (see above).
 * @param locale      Intl locales argument (default 'de-DE').
 * @returns The formatted string.
 */
export function normalizeDatetimeToString(
  normalized: { timestamp: number, day: boolean, month: boolean, year: boolean, time: boolean, notime: boolean },
  format: string,
  options: Intl.DateTimeFormatOptions = null,
  locale: string | string[] = 'de-DE'
): string {

  if (!normalized || typeof normalized.timestamp !== 'number') return '';
  const date = new Date(normalized.timestamp);

  // Build per-token formatters; inherit only safe options like timeZone/hour12
  // Note: TypeScript's Intl.DateTimeFormatOptions in our environment doesn't include
  // `calendar` or `numberingSystem`. To keep compilation strict, we only type `timeZone` and `hour12`.
  const base: Pick<Intl.DateTimeFormatOptions, 'timeZone' | 'hour12'> = {};
  if (options?.timeZone) base.timeZone = options.timeZone;
  if (typeof options?.hour12 === 'boolean') base.hour12 = options.hour12;
  // Pass through optional properties when supported by the runtime; avoid typing errors.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const baseExtended: any = base;
  if ((options as any)?.calendar) baseExtended.calendar = (options as any).calendar;
  if ((options as any)?.numberingSystem) baseExtended.numberingSystem = (options as any).numberingSystem;

  const yearStyle = (options?.year as Intl.DateTimeFormatOptions['year']) || 'numeric';
  const monthWidth = (options?.month as Intl.DateTimeFormatOptions['month']) || 'short';
  const dayWidth = (options?.day as Intl.DateTimeFormatOptions['day']) || 'numeric';
  const weekdayWidth = (options?.weekday as Intl.DateTimeFormatOptions['weekday']) || 'short';
  const hourStyle = (options?.hour as Intl.DateTimeFormatOptions['hour']) || 'numeric';
  const minuteStyle = (options?.minute as Intl.DateTimeFormatOptions['minute']) || '2-digit';
  const secondStyle = (options?.second as Intl.DateTimeFormatOptions['second']) || '2-digit';

  const fmt = (opts: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat(locale, { ...baseExtended, ...opts }).format(date);

  const replaceToken = (token: string): string => {
    switch (token) {
      case 'year': return fmt({ year: yearStyle });
      case 'month': return fmt({ month: monthWidth });
      case 'day': return fmt({ day: dayWidth });
      case 'date': return fmt({ day: dayWidth, month: monthWidth });
      case 'weekday': return fmt({ weekday: weekdayWidth });
      case 'hour': return fmt({ hour: hourStyle });
      case 'minute': return fmt({ minute: minuteStyle });
      case 'seconds': return fmt({ second: secondStyle });
      case 'time': return fmt({ hour: hourStyle, minute: minuteStyle });
      default: return '{' + token + '}';
    }
  };

  // Recursive processor for conditionals and replacements
  // Notes:
  // - Accepted condition keys: year, month, day, time, notime.
  // - A trailing space after the key is required (e.g. "[time {time}]"). If the space is missing, or the key is unknown,
  //   the sequence is treated as literal text rather than a condition block. This keeps formats readable and fail-safe.
  const evalSection = (s: string, idx: number = 0): [string, number] => {
    let out = '';
    while (idx < s.length) {
      const ch = s[idx];
      if (ch === '[') {
        // Parse condition key, allow negation with leading '!'
        const start = idx; // remember to fallback to literal if not a valid condition block
        let i = idx + 1;
        let neg = false;
        if (s[i] === '!') { neg = true; i++; }
        let key = '';
        while (i < s.length && /[a-zA-Z]/.test(s[i])) { key += s[i]; i++; }

        // Require at least one space after the key for a valid condition block.
        // If missing space (e.g., "[time-" or "[time]") treat as literal '[' and continue.
        if (!key || i >= s.length || s[i] !== ' ') {
          out += '[';
          idx = start + 1;
          continue;
        }

        // Accept only known keys; unknown keys are treated as literal text
        const allowed = key === 'year' || key === 'month' || key === 'day' || key === 'time' || key === 'notime';
        if (!allowed) {
          out += '[';
          idx = start + 1;
          continue;
        }

        // Skip the single required space after the key
        i += 1;
        // Evaluate inner until matching ]
        const [inner, nextIdx] = evalSection(s, i);
        idx = nextIdx;
        const includeBase = (key === 'year' ? normalized.year
                           : key === 'month' ? normalized.month
                           : key === 'day' ? normalized.day
                           : key === 'time' ? normalized.time
                           : key === 'notime' ? normalized.notime
                           : false);
        const include = neg ? !includeBase : includeBase;
        if (include) out += inner;
      } else if (ch === ']') {
        // End of this section
        return [out, idx + 1];
      } else if (ch === '{') {
        // Replacement token
        const close = s.indexOf('}', idx + 1);
        if (close === -1) { out += ch; idx++; continue; }
        const token = s.slice(idx + 1, close).trim();
        out += replaceToken(token);
        idx = close + 1;
      } else {
        out += ch;
        idx++;
      }
    }
    return [out, idx];
  };

  const [result] = evalSection(format, 0);
  return result;
}

/**
 * Demonstrates normalizeDatetimeToString with a few common templates.
 */
export function demo_normalizeDatetimeToString(): Record<string, string> {
  const start = '2025-03-12T11:00:00';
  const end = '2025-03-12T12:30:00';
  const n1 = normalizeDatetime(start); // no reference
  const n2 = normalizeDatetime(start, end); // compare start vs end (same day)
  const n3 = normalizeDatetime('2025-03-13', '2025-03-12'); // different day, no time

  const opts: Intl.DateTimeFormatOptions = { timeZone: 'Europe/Berlin', month: 'short', minute: '2-digit' };

  return {
    single: normalizeDatetimeToString(n1, '[weekday {weekday}, ]{day}. {month} {year} [time {time}]', opts, 'de-DE'),
    sameDayRangeStart: normalizeDatetimeToString(n2, '{time}', opts, 'de-DE'),
    sameDayRangeEnd: normalizeDatetimeToString(normalizeDatetime(end, start), '{time}', opts, 'de-DE'),
    noTimeShowWeekday: normalizeDatetimeToString(n3, '[!time {weekday} ]{day}. {month} {year}', opts, 'de-DE'),
    withSeconds: normalizeDatetimeToString(normalizeDatetime('2025-03-12T09:05:07'), '{time}:{seconds}', { timeZone: 'Europe/Berlin', minute: '2-digit', second: '2-digit' }, 'de-DE'),
  };
}

/**
 * **import { shuffleArray } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Randomizes an array.
 * 
 * @param array   the array to be shuffled
 * @return returns the provided array
 * 
 */
export function shuffleArray(array: Array<any>): Array<any> {

  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }

  return array;
}

/**
 * Returns a shuffled copy of the original array using the Fisher-Yates algorithm.
 * This function is similar to {@link shuffleArray} but does not modify the original array.
 *
 * @param array   the array to be shuffled
 * @return a new array containing the shuffled elements
 */
export function immutableShuffleArray<T>(array: T[]) {
  
  return shuffleArray([...array]);
}

/**
 * **import { sortObject } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Sorts an object by its keys ascending.
 * Use the Object function "reverse()"  on the result to get a descending order.
 *
 * @param obj   the object to be ordered (will not be touched)
 * @return a sorted object
 */
export function sortObject(obj: object): object {

  const ordered = {};
  Object.keys(obj).sort().forEach(function(key) {
    ordered[key] = obj[key];
  });

  return ordered;
}

/**
 * **import { mergeObjects } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Creates a new object by merging two other objects.
 * Keys of the second object overwrite keys of the first.
 * Values in the second object which are empty arrays or result in false while not of type boolean are ignored.
 * This is a deep merge.
 *
 * The "ignore" object defines keys which will not be merged. If a key in the "ignore" object is set to true,
 * the value of the first object is taken if set, otherwise the value of the second object.
 * If the key in the "ignore" object is not set to true, the value of either objects is not copied over to the
 * merged object. Nested objects in the "ignore" object are possible to address deeper keys.
 *
 * @param obj_1   the first original object
 * @param obj_2   the second original object
 * @param ignore  keys in this object are ignored while merging (optional)
 * @return merged object
 */
export function mergeObjects(obj_1: object, obj_2: object, ignore: object = {}): object {

  if (typeof obj_1 != 'object' || obj_1 === null) {
    if ((typeof obj_2 != "boolean" && obj_2) || typeof obj_2 == "boolean") return mergeObjects(obj_2, null, ignore);
    return obj_1;
  }

  if (((typeof obj_2 != 'object' || obj_2 === null) && typeof obj_2 != "boolean" && obj_2) || typeof obj_2 == "boolean") return obj_2;

  if (Array.isArray(obj_1)) {
    if (Array.isArray(obj_2) && obj_2.length) return mergeObjects(obj_2, null);
    if ((!Array.isArray(obj_2) && typeof obj_2 != "boolean" && obj_2) || typeof obj_2 == "boolean") return mergeObjects(obj_2, null, ignore);

    const result = [];
    for (let i=0, l=obj_1.length; i<l; i++) result.push(mergeObjects(obj_1[i], null));
    return result;
  }

  if (Array.isArray(obj_2) && obj_2.length) return mergeObjects(obj_2, null);


  const result = {};
  for (let key in obj_1) {

    let next_ignore = {};
    if (Object.keys(ignore).includes(key)) {
      if (typeof ignore[key] == 'object' && ignore[key] !== null) next_ignore = ignore[key];
      else if (ignore[key] === true) result[key] = mergeObjects(obj_1[key], null, {});
      else continue;
    }

    if (obj_2 && !Array.isArray(obj_2) && Object.keys(obj_2).includes(key)) result[key] = mergeObjects(obj_1[key], obj_2[key], next_ignore);
    else result[key] = mergeObjects(obj_1[key], null, next_ignore);
  }

  if (!Array.isArray(obj_2)) {
    for (let key in obj_2) {
      if (Object.keys(result).includes(key)) continue;

      let next_ignore = {};
      if (Object.keys(ignore).includes(key)) {
        if (typeof ignore[key] == 'object' && ignore[key] !== null) next_ignore = ignore[key];
        else if (ignore[key] === true) result[key] = mergeObjects(obj_2[key], null, {});
        else continue;
      }

      result[key] = mergeObjects(obj_2[key], null, next_ignore);
    }
  }

  return result;
}

/**
 * **import { forceDomRender } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Forces the DOM to rerender the given element.
 * Usefull to fix render issues of the DOM.
 *
 * Uses the style "transform" of the element and its parent.
 * Any previously set value for "transform" will be unset.
 */
export function forceDOMRender(element: HTMLElement) {

  let offsetHeight = "offsetHeight", parentNode = element.parentNode, parentElement = element.parentElement;

  parentElement ? ((parentElement.style["webkit"+"Transform"] = 'translateZ(1px)') && (parentElement.style.transform = 'translateZ(1px)')) : ((element.style["webkit"+"Transform"] = 'translateZ(1px)') && (element.style.transform = 'translateZ(1px)'));
  parentNode && (offsetHeight in parentNode) ? parentNode[offsetHeight] : element[offsetHeight];

  window.setTimeout(() => {
    parentElement ? ((parentElement.style["webkit"+"Transform"] = 'none') && (parentElement.style.transform = 'none')) : ((element.style["webkit"+"Transform"] = 'none') && (element.style.transform = 'none'));
    parentNode && (offsetHeight in parentNode) ? parentNode[offsetHeight] : element[offsetHeight];
  }, 20);

  window.setTimeout(() => {
    parentElement ? ((parentElement.style["webkit"+"Transform"] = 'translateZ(2px)') && (parentElement.style.transform = 'translateZ(2px)')) : ((element.style["webkit"+"Transform"] = 'translateZ(2px)') && (element.style.transform = 'translateZ(2px)'));
    parentNode && (offsetHeight in parentNode) ? parentNode[offsetHeight] : element[offsetHeight];
  }, 50);

  window.setTimeout(() => {
    parentElement ? ((parentElement.style["webkit"+"Transform"] = 'none') && (parentElement.style.transform = 'none')) : ((element.style["webkit"+"Transform"] = 'none') && (element.style.transform = 'none'));
    parentNode && (offsetHeight in parentNode) ? parentNode[offsetHeight] : element[offsetHeight];
  }, 100);

  window.setTimeout(() => {
    parentElement ? ((parentElement.style["webkit"+"Transform"] = 'translateZ(3px)') && (parentElement.style.transform = 'translateZ(3px)')) : ((element.style["webkit"+"Transform"] = 'translateZ(3px)') && (element.style.transform = 'translateZ(3px)'));
    parentNode && (offsetHeight in parentNode) ? parentNode[offsetHeight] : element[offsetHeight];
  }, 300);

  window.setTimeout(() => {
    parentElement ? ((parentElement.style["webkit"+"Transform"] = 'none') && (parentElement.style.transform = 'none')) : ((element.style["webkit"+"Transform"] = 'none') && (element.style.transform = 'none'));
    parentNode && (offsetHeight in parentNode) ? parentNode[offsetHeight] : element[offsetHeight];
  }, 1000);
}

/**
 * **import { createHash } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Creates a hash value for a string.
 */
export function createHash(value: string): number {

  let hash = 0, i: number, chr: number;
  for (i = 0; i < value.length; i++) {
    chr   = value.charCodeAt(i);
    hash  = ((hash << 5) - hash) + chr;
    hash |= 0; // Convert to 32bit integer
  }

  return hash;
}

/**
 * **import { getTextContent } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Returns the text content of the given element.
 * Takes "aria-hidden" and "aria-label" in consideration.
 */
export function getTextContent(element:Element): string {

  let text = '';

  const children = element.childNodes;
  for (let i=0, l=children.length; i<l; i++) {

    const type = children[i].nodeType;
    if (type == 2 || type == 4 || type == 8) continue;

    const value = children[i].nodeValue;
    if (value !== null) {
      if (value != '') text += value+' ';
    } else {
      const child = children[i] as Element;
      const hidden = child.getAttribute('aria-hidden');
      const label = child.getAttribute('aria-label');
      if (!hidden || hidden.toLowerCase() != 'true') {
        if (label) text += label+' ';
        else text += getTextContent(child);
      }
    }
  }

  return text;
}

/**
 * **import { windowScrollSmoothTo } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Scrolls the window smoothly.
 * Polyfills missing "smooth" functionality of certain browser (Safari).
 * 
 * @param   top   the top position
 * @param   left  the left position
 */
export function windowScrollSmoothTo(top: number, left?: number) {

  if (top === null || top === undefined) top = window.scrollY;
  if (left === null || left === undefined) left = window.scrollX;

  if ('scrollBehavior' in document.documentElement.style) {

    window.scrollTo({
      top: top,
      left: left,
      behavior: "smooth"
    });

  } else {

    if (window["HyloWindowScrollSmoothToStop"]) window["HyloWindowScrollSmoothToStop"]();

    const start_top = window.scrollY;
    const start_left = window.scrollX;

    const max_step = 200;
    const min_step = 5;
    let step_top = 0;
    let step_left = 0;

    let stopped = false;
    const stop = function() {
      stopped = true;
      window["HyloWindowScrollSmoothToStop"] = null;
    }
    window["HyloWindowScrollSmoothToStop"] = stop;

    const nextStep = () => {

      if (stopped) return;

      if (Math.abs(window.scrollY - top) <= min_step && Math.abs(window.scrollX - left) <= min_step) {
        window.scrollTo({
          top: top,
          left: left
        });

        return;
      }

      const current_top = window.scrollY;
      const current_left = window.scrollX;

      let increase_top = true;
      if (start_top >= top && (start_top - top) / 2 + top > current_top) increase_top = false;
      if (start_top < top && (top - start_top) / 2 + start_top < current_top) increase_top = false;

      let increase_left = true;
      if (start_left >= left && (start_left - left) / 2 + left > current_left) increase_left = false;
      if (start_left < left && (left - start_left) / 2 + start_left < current_left) increase_left = false;

      let actual_step_top = (current_top - top) / 2;
      if (increase_top) {
        step_top += min_step;
        if (step_top > max_step) step_top = max_step;
        if (Math.abs(actual_step_top) > step_top) actual_step_top = step_top * (actual_step_top / Math.abs(actual_step_top));
        if (Math.abs(actual_step_top) < min_step) actual_step_top = min_step * (actual_step_top / Math.abs(actual_step_top));
      } else {
        step_top -= min_step;
        if (step_top < min_step) step_top = min_step;
        if (Math.abs(actual_step_top) < step_top) actual_step_top = step_top * (actual_step_top / Math.abs(actual_step_top));
        if (Math.abs(actual_step_top) > max_step) actual_step_top = max_step * (actual_step_top / Math.abs(actual_step_top));
      }

      let actual_step_left = (current_left - left) / 2;
      if (increase_left) {
        step_left += min_step;
        if (step_left > max_step) step_left = max_step;
        if (Math.abs(actual_step_left) > step_left) actual_step_left = step_left * (actual_step_left / Math.abs(actual_step_left));
        if (Math.abs(actual_step_left) < min_step) actual_step_left = min_step * (actual_step_left / Math.abs(actual_step_left));
      } else {
        step_left -= min_step;
        if (step_left < min_step) step_left = min_step;
        if (Math.abs(actual_step_left) < step_left) actual_step_left = step_left * (actual_step_left / Math.abs(actual_step_left));
        if (Math.abs(actual_step_left) > max_step) actual_step_left = max_step * (actual_step_left / Math.abs(actual_step_left));
      }

      if (Math.abs(current_top - top) < min_step) actual_step_top = current_top - top;
      if (Math.abs(current_left - left) < min_step) actual_step_left = current_left - top;

      //console.log('step:', current_top, step_top, actual_step_top, increase_top);

      window.scrollTo({
        top: current_top - actual_step_top,
        left: current_left - actual_step_left
      });


      if (Math.abs(window.scrollY - top) > 1 || Math.abs(window.scrollX - left) > 1) {

        window.setTimeout(() => {
          nextStep();
        }, 10);

      } else {

        window.scrollTo({
          top: top,
          left: left
        });
      }

    }

    nextStep();
  }
}

/**
 * **import { windowScrollSmoothToHash } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Scrolls the window smoothly vertically to an element with the given hash as id.
 * Polyfills missing "smooth" functionality of certain browser (Safari).
 * 
 * Returns a promise which fulfils with the target element when the scrolling finished or with null if the target was invalid or the scrolling got interrupted by another windowScrollSmoothToHash call or a keydown event.
 * 
 * If the scrolling is triggered by an keydown event, don't forget to stop the propagation, otherwise the function will return null because the scrolling got interrupted.
 * 
 * @param hash            a hash id with or without leading "#" or the HTMLElement to scroll to
 * @param offset          an optional offset in pixel
 * @param focus           focuses the target element when scrolling finished
 * @param set_hash        sets the hash in the url of the browser when scrolling finished
 * @param create_history  creates a new browser history entry instead of replacing the current with the new hash tag
 * @return the target element
 */
export function windowScrollSmoothToHash(hash: string | HTMLElement, offset: number = 0, focus: boolean = false, set_hash: boolean = false, create_history: boolean = false): Promise<HTMLElement> {

  if (typeof hash == "string") {
    hash = hash.trim()
    if (!hash || hash === '#') return Promise.resolve(null);
  } else if (!hash) return Promise.resolve(null);

  let backup_timer = null;

  let resolve = null;
  const result: Promise<HTMLElement> = new Promise((resolved) => resolve = resolved);

  const to = typeof hash == "string" ? (hash[0] == '#' ? document.querySelector(CSS.escape(hash)) as HTMLElement : document.getElementById(hash)) : hash;
  if (to) {

    if (window["HyloWindowScrollSmoothToHashTerminate"]) window["HyloWindowScrollSmoothToHashTerminate"](true);

    let terminated = false;
    const terminate = function(killed: boolean = false) {

      if (backup_timer) window.clearTimeout(backup_timer);
      backup_timer = null;
      
      window["HyloWindowScrollSmoothToHashTerminate"] = null;
      window.removeEventListener('scroll', on_scroll);
      window.removeEventListener('keydown', on_keydown);

      if (terminated) return;
      terminated = true;

      if (killed) {
        resolve(null);
        return;
      }

      window.setTimeout(() => {

        if (set_hash) {
          let hash_tag = typeof hash == "string" ? hash : (hash.id ? hash.id : hash.getAttribute("name"));
          if (hash_tag) {
            if (hash_tag[0] != '#') hash_tag = '#'+hash_tag;
            if (create_history) window.location.hash = hash_tag;
            else {
              let params = (new URL(window.location as unknown as string)).searchParams;            
              const search =  params.toString() ? '?'+ params.toString() : '';
              window.history.replaceState(window.history.state, document.title, window.location.pathname + search + hash_tag);
            }
          }
        }

        if (focus) {
          const temp_tab = to.tabIndex;
          to.tabIndex = 0;
          to.focus();
          to.tabIndex = temp_tab;
        }

        window.scrollTo({
          top: top,
          left: window.scrollX
        });

        window.setTimeout(() => {
          if (Math.abs(top - window.scrollY) < 5 && Math.abs(to.getBoundingClientRect().top + offset) >= 5) {
            windowScrollSmoothToHash(hash, offset).then(() => resolve(to));
          } else resolve(to);
        }, 400);

      }, 100);

    }


    const on_scroll = function() {
      if (Math.abs(top - window.scrollY) < 10) terminate();
    }

    const on_keydown = function() {
      if (window["HyloWindowScrollSmoothToHashTerminate"]) window["HyloWindowScrollSmoothToHashTerminate"](true);
    }


    window.removeEventListener('scroll', on_scroll);
    window.removeEventListener('keydown', on_keydown);
    const top = to.getBoundingClientRect().top + offset + window.scrollY;

    if (Math.abs(top - window.scrollY) >= 10) {

      window["HyloWindowScrollSmoothToHashTerminate"] = terminate;

      window.addEventListener('scroll', on_scroll);
      window.addEventListener('keydown', on_keydown);
      windowScrollSmoothTo(top);
      if (backup_timer) window.clearTimeout(backup_timer);
      backup_timer = window.setTimeout(() => terminate(), Math.abs(top - window.scrollY));

    } else {

      if (window["HyloWindowScrollSmoothToStop"]) window["HyloWindowScrollSmoothToStop"]();

      if (set_hash) {
        let hash_tag = typeof hash == "string" ? hash : (hash.id ? hash.id : hash.getAttribute("name"));
        if (hash_tag) {
          if (hash_tag[0] != '#') hash_tag = '#'+hash_tag;
          if (create_history) window.location.hash = hash_tag;
          else {
            let params = (new URL(window.location as unknown as string)).searchParams;            
            const search =  params.toString() ? '?'+ params.toString() : '';
            window.history.replaceState(window.history.state, document.title, window.location.pathname + search + hash_tag);
          }
        }
      }

      if (focus) {
        const temp_tab = to.tabIndex;
        to.tabIndex = 0;
        to.focus();
        to.tabIndex = temp_tab;
      }

      window.scrollTo({
        top: top,
        left: window.scrollX
      });

      resolve(to);
    }

  } else {

    resolve(null);
  }

  return result;
}

/**
 * **import { windowJumpToHash } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Moves the window vertically to an element with the given hash as id.
 * 
 * Doesn't do anything if hash is empty or just "#".
 * 
 * @param hash            a hash id with or without leading "#" or the HTMLElement to scroll to
 * @param offset          an optional offset in pixel
 * @param focus           focuses the target element when scrolling finished
 * @param set_hash        sets the hash in the url of the browser when scrolling finished
 * @param create_history  creates a new browser history entry instead of replacing the current with the new hash tag
 */
export function windowJumpToHash(hash: string | HTMLElement, offset: number = 0, focus: boolean = false, set_hash: boolean = false, create_history: boolean = false) {

  if (typeof hash == "string") {
    hash = hash.trim()
    if (!hash || hash === '#') return;
  } else if (!hash) return;

  const to = typeof hash == "string" ? (hash[0] == '#' ? document.querySelector(CSS.escape(hash)) as HTMLElement : document.getElementById(hash)) : hash;
  if (to) {

    if (window["HyloWindowScrollSmoothToHashTerminate"]) window["HyloWindowScrollSmoothToHashTerminate"](true);
    if (window["HyloWindowScrollSmoothToStop"]) window["HyloWindowScrollSmoothToStop"]();

    const top = to.getBoundingClientRect().top + offset + window.scrollY;

    if (set_hash) {
      let hash_tag = typeof hash == "string" ? hash : (hash.id ? hash.id : hash.getAttribute("name"));
      if (hash_tag) {
        if (hash_tag[0] != '#') hash_tag = '#'+hash_tag;
        if (create_history) window.location.hash = hash_tag;
        else {
          let params = (new URL(window.location as unknown as string)).searchParams;            
          const search =  params.toString() ? '?'+ params.toString() : '';
          window.history.replaceState(window.history.state, document.title, window.location.pathname + search + hash_tag);
        }
      }
    }

    if (focus) {
      const temp_tab = to.tabIndex;
      to.tabIndex = 0;
      to.focus();
      to.tabIndex = temp_tab;
    }

    window.scrollTo({
      top: top,
      left: window.scrollX
    });

  }

}

/**
 * **import { isHidden } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Checks if an element's display style or one of its ancestors is "none".
 */
export function isHidden(element: HTMLElement) {

  if (element.offsetParent) return false;
  if (element.clientWidth || element.clientHeight) return false;
  if (element == document.body) return false;

  return true;
}

/**
 * **import { disableKeyboardInteractivity } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Disables the keyboard interactivity of links, buttons, hylo-router-link, hylo-button, 
 * hylo-accessibility-button components inside the scope element.
 * 
 * Prevents keyboard lock of hidden HTMLElements (display: none) which contain interactive elements.
 * 
 * Optional the function searches for HTMLElements which have the style "display: none" inside the 
 * scope first and than calls itself with these elements as new scope.
 * 
 * @param scope         a HTMLElement which defines the scope/parent of the action
 * @param display_none  find all HTMLElements with "display: none" inside the scope first
 * @param additional    a css query defining additional HTMLElements to disable
 */
export function disableKeyboardInteractivity(scope: HTMLElement, display_none: boolean = false, additional?: string) {


  if (display_none && !isHidden(scope)) {

    const children = (element: HTMLElement) => {

      const elements = element.children;

      for (let i=0, l=element.childElementCount; i<l; i++) {
        const check = elements[i] as HTMLElement;
        if (isHidden(check)) {
          if (Number.isNaN(Number.parseInt(check.getAttribute('data-disabled')))) {
            check.setAttribute('data-disabled', '1');
            disableKeyboardInteractivity(check);
          }
        } else children(check);
      }
    }

    children(scope);
    return;
  }



  const includes = (element: Element, elements: NodeListOf<Element>) => {
    for (let i=0, l=elements.length; i<l; i++) {
      if (elements[i] == element) return true;
    }
    return false;
  }

  const tabindex = (element: HTMLElement) => {
    let tabindex = Number.parseInt(element.getAttribute('data-disabled-tabindex'));
    if (Number.isNaN(tabindex)) {
      tabindex = element.tabIndex+1;
      if (tabindex < 0) tabindex = 0;
      if (Number.isNaN(Number.parseInt(element.getAttribute('tabindex')))) tabindex = -1;
    }
    element.setAttribute('data-disabled-tabindex', tabindex+'');
    element.tabIndex = -1;
  }

  const tabboolean = (element: Element) => {
    const el = element as any;
    let tabboolean = Number.parseInt(el.getAttribute('data-disabled-tabboolean'));
    if (Number.isNaN(tabboolean)) tabboolean = el.disabled ? 1 : 0;
    el.setAttribute('data-disabled-tabboolean', tabboolean+'');
    el.disabled = true;
  }

  const hylo_accessibility_buttons = scope.querySelectorAll('hylo-accessibility-button');
  const hylo_links = scope.querySelectorAll('hylo-router-link');
  const hylo_buttons = scope.querySelectorAll('hylo-button');
  const links = scope.querySelectorAll('a');
  const buttons = scope.querySelectorAll('button');
  const additionals = additional?.trim() ? scope.querySelectorAll(additional) : [];

  for (let i=0, l=links.length; i<l; i++) {
    const hylo_link = links[i].closest('hylo-router-link');
    if (hylo_link && includes(hylo_link, hylo_links)) continue;
    const hylo_accessibility_button = links[i].closest('hylo-accessibility-button');
    if (hylo_accessibility_button && includes(hylo_accessibility_button, hylo_accessibility_buttons)) continue;
    tabindex(links[i]);
  }

  for (let i=0, l=buttons.length; i<l; i++) {
    const hylo_button = buttons[i].closest('hylo-button');
    if (hylo_button && includes(hylo_button, hylo_buttons)) continue;
    const hylo_accessibility_button = buttons[i].closest('hylo-accessibility-button');
    if (hylo_accessibility_button && includes(hylo_accessibility_button, hylo_accessibility_buttons)) continue;
    tabindex(buttons[i]);
  }

  for (let i=0, l=additionals.length; i<l; i++) {
    const element = additionals[i] as HTMLElement;
    const hylo_link = element.closest('hylo-router-link');
    if (hylo_link && includes(hylo_link, hylo_links)) continue;
    const hylo_button =element.closest('hylo-button');
    if (hylo_button && includes(hylo_button, hylo_buttons)) continue;
    const hylo_accessibility_button = element.closest('hylo-accessibility-button');
    if (hylo_accessibility_button && includes(hylo_accessibility_button, hylo_accessibility_buttons)) continue;
    tabindex(element);
  }

  for (let i=0, l=hylo_links.length; i<l; i++) tabboolean(hylo_links[i]);
  for (let i=0, l=hylo_buttons.length; i<l; i++) tabboolean(hylo_buttons[i]);
  for (let i=0, l=hylo_accessibility_buttons.length; i<l; i++) tabboolean(hylo_accessibility_buttons[i]);
}

/**
 * **import { enableKeyboardInteractivity } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Enables keyboard interactivity of links, buttons, hylo-router-link, hylo-button, 
 * hylo-accessibility-button components inside the scope element which were previously disabled 
 * by disableKeyboardInteractivity.
 * 
 * @param scope   a HTMLElement which defines the scope/parent of the action
 */
export function enableKeyboardInteractivity(scope: HTMLElement) {

  const display = scope.querySelectorAll('*[data-disabled]');
  for (let i=0, l=display.length; i<l; i++) {
    const element = display[i] as HTMLElement;
    element.removeAttribute('data-disabled');
    enableKeyboardInteractivity(element);
  }

  const tabindex = scope.querySelectorAll('*[data-disabled-tabindex]');
  for (let i=0, l=tabindex.length; i<l; i++) {
    const element = tabindex[i] as HTMLElement;
    const index = Number.parseInt(element.getAttribute('data-disabled-tabindex'));
    element.removeAttribute('data-disabled-tabindex');
    if (index == -1) element.removeAttribute('tabindex');
    else element.tabIndex = index-1;
  }

  const tabboolean = scope.querySelectorAll('*[data-disabled-tabboolean]');
  for (let i=0, l=tabboolean.length; i<l; i++) {
    const element = tabboolean[i] as any;
    const index = Number.parseInt(element.getAttribute('data-disabled-tabboolean'));
    element.removeAttribute('data-disabled-tabboolean');
    element.disabled = !!index;
  }
}

/**
 * **import { hyloComponentsLoaded } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Gathers components which fired the event "hyloComponentLoaded". It does not check if components already fired the event.
 * It listens on the "parent" until the initial "timespan" runs out and no component got gather. 
 * Every time a component gets gathered the timespan is "reset".
 * The gathering can be restricted to specific tag names.
 * 
 * @param parent    the element on wich is listend for the "hyloComponentLoaded" event
 * @param tags      an array of tag names to which the gathering is restricted to (optional)
 * @param timespan  the initial listening timespan in milliseconds (optional)
 * @param reset     the reset listening timespan in milliseconds (optional)
 */
export function hyloComponentsLoaded(parent: HTMLElement, tags: string[] = [], timespan: number = 5000, reset: number = 1000): Promise<HTMLElement[]> {

  let loaded_resolve: Function;
  const loaded_promise: Promise<undefined> = new Promise((resolve) => {loaded_resolve = resolve});

  let timer: number;
  const images: HTMLElement[] = [];

  const loaded = function(event: CustomEvent) {

    const target = event.target as HTMLElement;

    let tag = !tags.length;
    for (let i=0, l=tags.length; i<l; i++) {
      if (target.tagName.toLowerCase() == tags[i].toLowerCase()) {
        tag = true;
        break;
      }
    }
    if (!tag) return;

    window.clearTimeout(timer);
    images.push(target);

    timer = window.setTimeout(() => {
      parent.removeEventListener('hyloComponentLoaded', loaded);
      loaded_resolve(images);
    }, reset);
  }

  if (!parent) loaded_resolve(images);
  else {
    
    timer = window.setTimeout(() => {
      parent.removeEventListener('hyloComponentLoaded', loaded);
      loaded_resolve(images);
    }, timespan);

    parent.addEventListener('hyloComponentLoaded', loaded);
  }

  return loaded_promise;
}

/**
 * **import { log } from '@src/hylo-components/utils/usefull/usefull';**
 *
 * Creates a logging console as part of the website.
 * A click on the logging console closes it again.
 * The value is also written to the actual debug console.
 * 
 * BTW: For Chrome or Edge on iOS open a tab and go to "about:inspect" to get the actual console log.
 * 
 * @param value     the value to be logged, an array is joined by " | "
 * @param type      the type of the log, default is "log"
 * @param styles    additional css styles added to the console
 */
export function log(value: any, type?: "log" | "warn" | "error", styles?: any) {

  let text: string = '';
  if (Array.isArray(value)) text = value.join(' | ');
  else if (typeof value == "object") text = JSON.stringify(value, null, 2);
  else text = value+'';

  if (window.console) {
    if (type == "warn") console.warn(text);
    else if (type == "error") console.error(text);
    else console.log(text);
  }

  let logging: HTMLElement = document.getElementById('hylo_logging');
  if (!logging) {
    logging = document.createElement('div');
    logging.id = 'hylo_logging';
    logging.style.display = 'none';
    logging.style.width = '100%';
    logging.style.height = '33%';
    logging.style.position = 'fixed';
    logging.style.left = '0px';
    logging.style.right = '0px';
    logging.style.bottom = '0px';
    logging.style.zIndex = '2147483647';
    logging.style.border = '2px solid black';
    logging.style.padding = '0px 10px';
    logging.style.background = 'white';
    logging.style.color = 'black';
    logging.style.fontFamily = 'monospace, monospace';
    logging.style.fontWeight = 'regular';
    logging.style.fontSize = '16px';
    logging.style.lineHeight = '1.25';
    logging.style.overflowY = 'auto';

    if (type == 'warn') logging.style.color = 'green';
    if (type == 'error') logging.style.color = 'red';

    if (styles) {
      for (let key in styles) logging.style[key] = styles[key];
    }

    document.body.append(logging)
    window.setTimeout(() => logging.style.display = 'block', 300);

    const click = () => logging.remove();
    logging.addEventListener('click', click);
  }

  const content = document.createElement('div');
  content.style.display = 'block';
  content.style.width = '100%';
  content.style.padding = '4px 0';
  content.innerHTML = text;
  logging.append(content);

  logging.scrollTo(0, logging.scrollHeight);
}

/**
 * Helper function to apply several classes conditionally on the same element
 * @param args
 * @returns 
 */
export function classNames(...args: any) {
  const classes = [];
  for (const arg of args) {
    if (!arg) continue;
    switch (typeof arg) {
      case 'string':
      case 'number':
        classes.push(arg);
        break;
      case 'object':
        if (Array.isArray(arg)) {
          classes.push(classNames(...arg));
        } else {
          for (const key in arg) {
            if (Object.prototype.hasOwnProperty.call(arg, key) && arg[key]) {
              classes.push(key);
            }
          }
        }
        break;
    }
  }
  return classes.join(' ');
}

/**
 * Get the current position of the user
 * @returns GeolocationPosition
 */
export function getCurrentPosition(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject);
  });
}

export function geoDistanceHumanized(pointA: GeolocationCoordinates, pointB: GeolocationCoordinates, locale = 'en-Us', unitSystem = 'metric') {
  const toRad = (degree: number) => (degree * Math.PI) / 180;

  const deltaLatitude = toRad(pointA.latitude - pointB.latitude);
  const deltaLongitude = toRad(pointA.longitude - pointB.longitude);

  const squareOfHalfChordLength = Math.pow(Math.sin(deltaLatitude / 2), 2) + Math.pow(Math.sin(deltaLongitude / 2), 2) * Math.cos(toRad(pointA.latitude)) * Math.cos(toRad(pointB.latitude));

  const hav = 2 * Math.atan2(Math.sqrt(squareOfHalfChordLength), Math.sqrt(1 - squareOfHalfChordLength));

  const result =
    unitSystem === 'metric'
      ? { distance: hav * 6373, unit: 'km', smallUnit: 'm', factor: 1000, smallBorder: 0.9 }
      : { distance: hav * 3960, unit: 'mi', smallUnit: 'yd', factor: 1760, smallBorder: 0.5 };

  const formatter = new Intl.NumberFormat(locale, { maximumSignificantDigits: 2 });
  
  if (result.distance < result.smallBorder) {
    let distance = result.distance * result.factor;
    if (distance < 40) return `< 50 ${result.smallUnit}`;

    distance = Math.round(distance / 50) * 50;
    return `${formatter.format(distance)} ${result.smallUnit}`;
  }

  return `${formatter.format(result.distance)} ${result.unit}`;
}

/**
 * Create a random UUID
 *
 * uses the common v4 UUID format
 */
export function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Check if the device is capable of hover
 * 
 * @returns true if the device is capable of hover
 */
export function isHoverCapable() {
  return window.ontouchstart === undefined;
}

/**
 * **import { followRedirect } from '@src/hylo-components/utils/usefull/usefull';**
 * 
 * Checks the given url whether it is a server redirect or not.
 * Returns the final url of a redirection. Chained redirections are considered.
 * 
 * If no setup request is provided, a general request with default values and the request method "HEAD" is created.
 * 
 * @param url             the url to check
 * @param check_response  checks the responses and throws an error when not ok
 * @param request         a request which serves as setup/overwrite of default values
 * @param timeout         timeout in milliseconds (default: 10000ms)
 * @returns a promise with the final url
 */
export function followRedirect(url: string, check_response: boolean = true, request?: Request, timeout: number = 10000): Promise<string> {
  url = url.trim();
  
  if (!url.match(/^https?:\/\//i)) {
    return Promise.reject(new Error('Invalid URL'));
  }

  if (!request) request = new Request(url, {method: 'HEAD'});

  return new Promise((resolve, reject) => {

    const controller = new AbortController();
    const timeout_id = setTimeout(() => controller.abort(), timeout);

    const performFetch = async (fetch_url: string) => {
      const new_request = new Request(fetch_url, {
        method: request.method,
        headers: request.headers,
        credentials: request.credentials,
        cache: request.cache,
        redirect: 'follow',
        referrer: request.referrer,
        integrity: request.integrity,
        signal: controller.signal
      });

      try {

        const response = await fetch(new_request);

        clearTimeout(timeout_id);

        if (check_response && !response.ok) {
          throw new Error(`Response NOT ok for ${fetch_url}`);
        }
        
        resolve(response.redirected ? response.url : fetch_url);

      } catch (error) {

        clearTimeout(timeout_id);

        if (error.name === 'AbortError') {
          reject(new Error('Request timed out'));
        } else {
          reject(error);
        }
      }

    };

    performFetch(url);
  });
}
